import { useState, useEffect } from "react";
import { MdWarning, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useAlert } from "../../../hooks/useAlert";

const AlertsTableComponent = ({ vmId }) => {
  const [index, setIndex] = useState(0);
  const { getAlertsByVmId, alertsData, loading, error } = useAlert();

  useEffect(() => {
    if (vmId !== undefined) {
      getAlertsByVmId(vmId, index);
    }
  }, [vmId, index]);

  const handlePrevious = () => {
    if (alertsData?.hasPrevious) {
      setIndex(index - 1);
    }
  };

  const handleNext = () => {
    if (alertsData?.hasNext) {
      setIndex(index + 1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const numSeconds = parseInt(seconds);
    if (numSeconds < 60) {
      return numSeconds + "s";
    } else if (numSeconds < 3600) {
      const minutes = Math.floor(numSeconds / 60);
      const remainingSeconds = numSeconds % 60;
      if (remainingSeconds === 0) {
        return minutes + "m";
      }
      return minutes + "m " + remainingSeconds + "s";
    } else {
      const hours = Math.floor(numSeconds / 3600);
      const remainingMinutes = Math.floor((numSeconds % 3600) / 60);
      if (remainingMinutes === 0) {
        return hours + "h";
      }
      return hours + "h " + remainingMinutes + "m";
    }
  };

  const formatMetricValue = (alertName, metricValue) => {
    if (metricValue === undefined || metricValue === null) return "0";
    const nameLower = alertName.toLowerCase();
    if (nameLower.includes("cpu")) {
      return `${metricValue}%`;
    } else if (nameLower.includes("ram") || nameLower.includes("memory")) {
      return `${metricValue} MB`;
    }
    return metricValue;
  };

  const getStatusBadge = (condition) => {
    if (condition === "Fired") {
      return (
        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium border bg-red-500/20 text-red-400 border-red-500 whitespace-nowrap">
          Fired
        </span>
      );
    }
    return (
      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium border bg-green-500/20 text-green-400 border-green-500 whitespace-nowrap">
        Resolved
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6 mt-4">
        <div className="text-center py-6 sm:py-8">
          <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm sm:text-base mt-2 sm:mt-3">Loading alerts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6 mt-4">
        <div className="text-center py-6 sm:py-8">
          <p className="text-red-400 text-sm sm:text-base">Error loading alerts: {error}</p>
        </div>
      </div>
    );
  }

  const alerts = alertsData?.alerts || [];
  const hasNext = alertsData?.hasNext || false;
  const hasPrevious = alertsData?.hasPrevious || false;
  const vmNameHeader = alertsData?.vmName || "All VMs";
  const totalCount = alertsData?.totalCount || 0;
  const showVmColumn = vmId === 0;

  return (
    <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6 mt-4">
      <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-6 pb-2 sm:pb-3 border-b border-gray-700">
        <MdWarning className="text-primary-cyan text-xl sm:text-2xl" />
        <h3 className="text-lg sm:text-xl font-semibold text-white">Alerts</h3>
        <span className="text-xs sm:text-sm text-gray-400 ml-0 sm:ml-2 break-words">
          ({vmNameHeader} - Total: {totalCount})
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm min-w-[700px]">
          <thead>
            <tr className="border-b border-gray-700">
              {showVmColumn && (
                <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium">
                  VM
                </th>
              )}
              <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium">
                Alert
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium hidden md:table-cell">
                Description
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium hidden lg:table-cell">
                Occurred At
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium">
                Value
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium hidden xl:table-cell">
                Resolved At
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium">
                Duration
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium">
                Status
              </th>
             </tr>
          </thead>
          <tbody>
            {alerts.map((alert, idx) => (
              <tr
                key={idx}
                className="border-b border-gray-700/50 hover:bg-white/5 transition cursor-pointer"
              >
                {showVmColumn && (
                  <td className="py-2 sm:py-3 px-2 sm:px-3">
                    <span className="text-white font-medium text-[10px] sm:text-xs break-words">
                      {alert.vmName || "N/A"}
                    </span>
                   </td>
                )}
                <td className="py-2 sm:py-3 px-2 sm:px-3">
                  <span className="text-white font-medium text-[10px] sm:text-xs break-words">
                    {alert.alertName}
                  </span>
                 </td>
                <td className="py-2 sm:py-3 px-2 sm:px-3 hidden md:table-cell">
                  <span className="text-gray-300 text-[10px] sm:text-xs break-words">
                    {alert.description || "N/A"}
                  </span>
                 </td>
                <td className="py-2 sm:py-3 px-2 sm:px-3 hidden lg:table-cell">
                  <span className="text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
                    {formatDate(alert.occurredAt)}
                  </span>
                 </td>
                <td className="py-2 sm:py-3 px-2 sm:px-3">
                  <span className="text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
                    {formatMetricValue(alert.alertName, alert.metricValue)}
                  </span>
                 </td>
                <td className="py-2 sm:py-3 px-2 sm:px-3 hidden xl:table-cell">
                  <span className="text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
                    {alert.resolvedAt ? formatDate(alert.resolvedAt) : "-"}
                  </span>
                 </td>
                <td className="py-2 sm:py-3 px-2 sm:px-3">
                  <span className="text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
                    {alert.duration ? formatDuration(alert.duration) : "0s"}
                  </span>
                 </td>
                <td className="py-2 sm:py-3 px-2 sm:px-3">
                  {getStatusBadge(alert.monitorCondition)}
                 </td>
               </tr>
            ))}
          </tbody>
        </table>
      </div>

      {alerts.length === 0 && (
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-400 text-sm sm:text-base">No alerts found for this VM</p>
        </div>
      )}

      {(totalCount > 6 || hasNext || hasPrevious) && (
        <div className="flex justify-center items-center gap-2 sm:gap-4 mt-4 sm:mt-6 pt-4 border-t border-gray-700">
          <button
            onClick={handlePrevious}
            disabled={!hasPrevious}
            className={`
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition text-xs sm:text-sm
              ${
                hasPrevious
                  ? "bg-primary-cyan hover:bg-primary-cyan/80 text-white"
                  : "bg-gray-700 text-gray-400 cursor-not-allowed"
              }
            `}
          >
            <MdChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Prev
          </button>

          <span className="text-gray-400 text-xs sm:text-sm">Page {index + 1}</span>

          <button
            onClick={handleNext}
            disabled={!hasNext}
            className={`
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition text-xs sm:text-sm
              ${
                hasNext
                  ? "bg-primary-cyan hover:bg-primary-cyan/80 text-white"
                  : "bg-gray-700 text-gray-400 cursor-not-allowed"
              }
            `}
          >
            Next
            <MdChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AlertsTableComponent;